import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from '@/components/ui/sheet';
import { MessageSquare, Send, X, Maximize2, Minimize2 } from 'lucide-react';
import { useChat } from '@/hooks/useChat';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ChatSessionSuggestion from './ChatSessionSuggestion';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  sessionSuggestion?: {
    presetId: string;
    customSession: {
      frequency: number;
      duration: number;
      ambientSounds: string[];
      soundscapes: string[];
    };
  };
}

interface ChatPanelProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onStartCustomSession?: (session: {
    frequency: number;
    duration: number;
    ambientSounds: string[];
    soundscapes: string[];
  }) => void;
  onStartPreset?: (presetId: string) => void;
}

const QUICK_ACTIONS = [
  // Session Recommendations
  'Recommend a session based on my needs',
  
  // General Questions
  'What are the different brainwave frequencies?',
  'How do binaural beats work?',
  'Tips for better meditation',
  'What are the benefits of binaural beats?',
  'How long should I listen to binaural beats?'
];

const TypingIndicator = () => (
  <div className="flex items-center gap-1 text-muted-foreground">
    <div className="w-2 h-2 rounded-full bg-current animate-bounce [animation-delay:-0.3s]" />
    <div className="w-2 h-2 rounded-full bg-current animate-bounce [animation-delay:-0.15s]" />
    <div className="w-2 h-2 rounded-full bg-current animate-bounce" />
  </div>
);

const MarkdownContent = ({ content }: { content: string }) => (
  <div className="prose prose-sm dark:prose-invert max-w-none">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="mb-2">{children}</p>,
        ul: ({ children }) => <ul className="list-disc pl-4 mb-2">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-4 mb-2">{children}</ol>,
        li: ({ children }) => <li className="mb-1">{children}</li>,
        h1: ({ children }) => <h1 className="text-xl font-bold mb-2">{children}</h1>,
        h2: ({ children }) => <h2 className="text-lg font-bold mb-2">{children}</h2>,
        h3: ({ children }) => <h3 className="text-base font-bold mb-2">{children}</h3>,
        code: ({ children }) => (
          <code className="bg-muted px-1 py-0.5 rounded text-sm font-mono">{children}</code>
        ),
        pre: ({ children }) => (
          <pre className="bg-muted p-2 rounded mb-2 overflow-x-auto">{children}</pre>
        ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-2 border-primary pl-4 italic mb-2">{children}</blockquote>
        ),
        a: ({ children, href }) => (
          <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
);

export const ChatPanel = ({ 
  isOpen, 
  onOpenChange, 
  onStartCustomSession,
  onStartPreset 
}: ChatPanelProps) => {
  const [input, setInput] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const { messages, sendMessage, isLoading } = useChat();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isUserScrolling = useRef(false);
  const shouldAutoScroll = useRef(true);

  // Handle scroll events to detect user scrolling
  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollArea;
      const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 50;
      shouldAutoScroll.current = isAtBottom;
    };

    scrollArea.addEventListener('scroll', handleScroll);
    return () => scrollArea.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to bottom when new messages arrive or content streams in
  useEffect(() => {
    if (shouldAutoScroll.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    await sendMessage(input);
    setInput('');
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className={cn(
          "p-0 transition-all duration-300 [&>button]:hidden !max-w-none",
          isExpanded 
            ? "w-full sm:!w-[50vw] lg:!w-[60vw] xl:!w-[70vw] !max-w-[1200px]" 
            : "w-full sm:!w-[400px]"
        )}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <div className="flex h-full flex-col">
          <SheetHeader className="p-4 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                <SheetTitle>Brainwave Assistant</SheetTitle>
                <span className="text-xs font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                  AI
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="h-8 w-8"
                >
                  {isExpanded ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onOpenChange?.(false)}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <SheetDescription className="mt-1">
              Ask questions about brainwaves, meditation, or get personalized session recommendations.
            </SheetDescription>
          </SheetHeader>
          
          <ScrollArea ref={scrollAreaRef} className="flex-1 p-4">
            <div className={cn(
              "space-y-4",
              isExpanded && "max-w-4xl mx-auto"
            )}>
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={cn(
                    'flex w-full',
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      'rounded-lg p-3',
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted',
                      isExpanded ? 'max-w-[95%]' : 'max-w-[80%]'
                    )}
                  >
                    <MarkdownContent content={message.content} />
                    {message.isStreaming && (
                      <div className="mt-2">
                        <TypingIndicator />
                      </div>
                    )}
                    {message.sessionSuggestion && (
                      <div className="mt-4">
                        <ChatSessionSuggestion
                          presetId={message.sessionSuggestion.presetId}
                          customSession={message.sessionSuggestion.customSession}
                          onStartCustomSession={onStartCustomSession}
                          onStartPreset={onStartPreset}
                          onClose={() => onOpenChange(false)}
                        />
                      </div>
                    )}
                    <span className="text-xs opacity-70 mt-1 block">
                      {message.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          <div className="p-4 border-t">
            <div className={cn(
              "flex flex-col gap-4",
              isExpanded && "max-w-4xl mx-auto"
            )}>
              <form onSubmit={handleSubmit} className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about brainwaves, meditation, or sessions..."
                  disabled={isLoading}
                />
                <Button type="submit" disabled={isLoading}>
                  <Send className="h-4 w-4" />
                </Button>
              </form>
              <div>
                <h3 className="text-sm font-medium mb-2">Quick Actions</h3>
                <div className="flex flex-wrap gap-2">
                  {QUICK_ACTIONS.map((action, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (action === 'Recommend a session based on my needs') {
                          sendMessage("I'd like you to recommend a session. Could you ask me a few questions about how I'm feeling and what I want to achieve?");
                        } else {
                          sendMessage(action);
                        }
                      }}
                      disabled={isLoading}
                    >
                      {action}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}; 